import React, { useState, useEffect, useRef } from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import {
    getMyProjects, getProjectTasks, Project, Task, saveDraft, getDraft, submitTimesheet, exportTimesheetXls,
    getTimesheetTemplates, getTimesheetTemplateDetail, TimesheetTemplateSummary, TimesheetTemplateLine,
} from "../services/api";

interface TimeEntry {
    id: string;
    projectId: number | null;
    taskId: number | null;
    hours: string;
    comments: string;
    // Only populated for lines loaded from an existing (non-draft) timesheet.
    // Undefined for brand-new/unsaved lines.
    lineStatus?: string;
    rejectionReason?: string | null;
}

interface DayEntries {
    date: Date;
    entries: TimeEntry[];
    isExpanded: boolean;
}

const getMonday = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

// 2026-08-04 (month-end split periods, Friday-exception added same day):
// mirrors backend/app/services/period_utils.py's get_period_bounds -- keep
// both in sync if this ever changes. A normal period is still a plain
// Mon-Sun week; the only change is that whenever that natural week's
// Monday and Sunday fall in different calendar months, the week splits
// into two shorter sub-periods (end-of-month side, start-of-next-month
// side) instead of one 7-day period straddling two months -- unless the
// last day of the month is a Friday, in which case Mon-Fri is already a
// full work week and the week isn't split (the leftover Sat-Sun would be
// a pointless weekend-only sub-period).
const getPeriodBounds = (date: Date): { start: Date; end: Date } => {
    const monday = getMonday(date);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);

    if (monday.getMonth() === sunday.getMonth() && monday.getFullYear() === sunday.getFullYear()) {
        return { start: monday, end: sunday };
    }

    // Straddles a month boundary -- but not if the last day of the month
    // (Monday's month) is a Friday.
    const lastDayOfMondayMonth = new Date(monday.getFullYear(), monday.getMonth() + 1, 0);
    lastDayOfMondayMonth.setHours(0, 0, 0, 0);
    if (lastDayOfMondayMonth.getDay() === 5) {
        return { start: monday, end: sunday };
    }

    // Which side is `date` on?
    if (date.getMonth() === monday.getMonth() && date.getFullYear() === monday.getFullYear()) {
        return { start: monday, end: lastDayOfMondayMonth };
    } else {
        const firstDay = new Date(sunday.getFullYear(), sunday.getMonth(), 1);
        firstDay.setHours(0, 0, 0, 0);
        return { start: firstDay, end: sunday };
    }
};

const getPeriodDates = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const d = new Date(start);
    while (d <= end) {
        dates.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return dates;
};

const nextPeriodBounds = (end: Date): { start: Date; end: Date } => {
    const next = new Date(end);
    next.setDate(next.getDate() + 1);
    return getPeriodBounds(next);
};

const prevPeriodBounds = (start: Date): { start: Date; end: Date } => {
    const prev = new Date(start);
    prev.setDate(prev.getDate() - 1);
    return getPeriodBounds(prev);
};

const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
};

const isFuture = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
};

const newEntry = (): TimeEntry => ({
    id: Math.random().toString(36).substr(2, 9),
    projectId: null,
    taskId: null,
    hours: "",
    comments: "",
});

const inputStyle: React.CSSProperties = {
    width: "100%", padding: "6px 8px", fontSize: "13px",
    border: "1px solid #ddd", borderRadius: "4px", boxSizing: "border-box",
};
const errorStyle: React.CSSProperties = { color: "#C00000", fontSize: "11px", marginTop: "2px" };
const navBtnStyle: React.CSSProperties = {
    backgroundColor: "white", border: "1px solid #ddd", borderRadius: "4px",
    padding: "6px 12px", cursor: "pointer", fontSize: "13px", color: "#5C5D5E",
};
const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 24px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "10px 24px", cursor: "pointer", fontSize: "14px",
};
const addLineBtnStyle: React.CSSProperties = {
    backgroundColor: "transparent", color: "#F18E00", border: "1px dashed #F18E00",
    borderRadius: "4px", padding: "6px 16px", cursor: "pointer", fontSize: "13px", marginTop: "4px",
};
const thStyle: React.CSSProperties = {
    padding: "8px 12px", textAlign: "center", border: "1px solid #4a4b4c", fontSize: "12px",
};
const tdStyle: React.CSSProperties = {
    padding: "8px 12px", textAlign: "center", border: "1px solid #e0e0e0", fontSize: "13px",
};

// A rejection-notification email deep-links here with ?period_start=YYYY-MM-DD
// (the timesheet's own period start -- not necessarily a Monday now that
// month-end periods can start on the 1st of a month) so the consultant
// lands directly on the period that needs fixing instead of always
// defaulting to the current period.
const getInitialPeriodBounds = (periodStartParam: string | null): { start: Date; end: Date } => {
    if (periodStartParam) {
        const parsed = new Date(periodStartParam + "T00:00:00");
        if (!isNaN(parsed.getTime())) return getPeriodBounds(parsed);
    }
    return getPeriodBounds(new Date());
};

const TimesheetEntry: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [weekStart, setWeekStart] = useState<Date>(() =>
        getInitialPeriodBounds(searchParams.get("period_start")).start
    );
    const [periodEnd, setPeriodEnd] = useState<Date>(() =>
        getInitialPeriodBounds(searchParams.get("period_start")).end
    );
    const [days, setDays] = useState<DayEntries[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [taskCache, setTaskCache] = useState<Record<number, Task[]>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [hasErrors, setHasErrors] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [applyPickerEntryId, setApplyPickerEntryId] = useState<string | null>(null);
    const [applyPickerDays, setApplyPickerDays] = useState<Record<number, boolean>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Holds the real backend error detail from the last failed draft save, so
    // handleConfirmSubmit can surface it instead of a generic message.
    const lastSaveErrorRef = useRef<string | null>(null);
    // Status of the timesheet (if any) that already exists for the week being
    // viewed. Null means "no timesheet yet for this week" (normal editable
    // state). Anything other than "draft" means this week was already
    // submitted/approved/rejected, so the grid is rendered read-only.
    const [timesheetStatus, setTimesheetStatus] = useState<string | null>(null);
    const [timesheetSubmittedAt, setTimesheetSubmittedAt] = useState<string | null>(null);
    // Only ever populated when timesheetStatus === "validation_rejected" --
    // the admin's single comment from a Check #1 whole-timesheet reject on
    // the Timesheet Validation screen, shown as a banner below.
    const [validationRejectionReason, setValidationRejectionReason] = useState<string | null>(null);
    // Backlog #5 (2026-08-10): "Export" button -- reuses the Zoho-upload
    // .xls format, only enabled once this timesheet has reached the
    // Timesheet Validation "Ready to Generate" stage (validation_status
    // "cleared") or later ("validated").
    const [timesheetId, setTimesheetId] = useState<number | null>(null);
    const [validationStatus, setValidationStatus] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    // Backlog #7 (2026-08-10): "Apply Template" -- loaded once (independent
    // of which week is being viewed, unlike the draft-loading effect below),
    // since templates aren't tied to a specific week.
    const [templates, setTemplates] = useState<TimesheetTemplateSummary[]>([]);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");
    const [applyingTemplate, setApplyingTemplate] = useState(false);
    const [templateError, setTemplateError] = useState<string | null>(null);
    // 2026-08-11 (Pete's explicit follow-up request): rather than silently
    // applying every template line to every weekday, selecting a template
    // now opens a day-picker modal -- one row per project/task combo in
    // the template, each with its own Mon-Sun checkboxes (same pattern as
    // the single-entry "→W" Apply-to-week picker above), so the user
    // controls exactly which days each line lands on before anything is
    // written into the grid.
    const [templateModalLines, setTemplateModalLines] = useState<TimesheetTemplateLine[] | null>(null);
    const [templateModalDayPicks, setTemplateModalDayPicks] = useState<Record<number, Record<number, boolean>>>({});
    const [applyingTemplateLines, setApplyingTemplateLines] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const projs = await getMyProjects();
                setProjects(projs);
            } catch (e) {
                console.error("Failed to load projects", e);
            } finally {
                setLoading(false);
            }
        };
        load();
        getTimesheetTemplates()
            .then(data => setTemplates(data.templates))
            .catch(e => console.error("Failed to load templates", e));
    }, []);

    useEffect(() => {
        const dates = getPeriodDates(weekStart, periodEnd);
        setDays(dates.map(date => ({
            date,
            entries: [newEntry()],
            isExpanded: !isFuture(date),
        })));
        setErrors({});
        setShowSummary(false);
        setSaveStatus(null);
        setIsDirty(false);
        setApplyPickerEntryId(null);
        setApplyPickerDays({});
        setTimesheetStatus(null);
        setTimesheetSubmittedAt(null);
        setValidationRejectionReason(null);
        setTimesheetId(null);
        setValidationStatus(null);
        setExportError(null);

        // Load existing draft for this week if one exists
        const loadDraft = async () => {
            try {
                const start = weekStart.toISOString().split("T")[0];
                const end = periodEnd.toISOString().split("T")[0];
                const result = await getDraft(start, end);
                if (result.timesheet) {
                    setTimesheetStatus(result.timesheet.status);
                    setTimesheetSubmittedAt(result.timesheet.submitted_at || null);
                    setValidationRejectionReason(result.timesheet.validation_rejection_reason || null);
                    setTimesheetId(result.timesheet.id);
                    setValidationStatus(result.timesheet.validation_status || null);
                }
                if (result.timesheet && result.timesheet.lines.length > 0) {
                    const dates2 = getPeriodDates(weekStart, periodEnd);
                    const newDays = dates2.map(date => ({
                        date,
                        entries: [newEntry()],
                        isExpanded: !isFuture(date),
                    }));
                    result.timesheet.lines.forEach((line: any) => {
                        const lineDate = new Date(line.entry_date + "T00:00:00");
                        const dayIdx = dates2.findIndex(d => d.toDateString() === lineDate.toDateString());
                        if (dayIdx === -1) return;
                        const lastEntry = newDays[dayIdx].entries[newDays[dayIdx].entries.length - 1];
                        if (lastEntry.projectId === null) {
                            newDays[dayIdx].entries[newDays[dayIdx].entries.length - 1] = {
                                ...lastEntry,
                                projectId: line.project_id,
                                taskId: line.task_id,
                                hours: line.hours.toString(),
                                comments: line.comments,
                                lineStatus: line.line_status,
                                rejectionReason: line.rejection_reason || null,
                            };
                        } else {
                            newDays[dayIdx].entries.push({
                                id: Math.random().toString(36).substr(2, 9),
                                projectId: line.project_id,
                                taskId: line.task_id,
                                hours: line.hours.toString(),
                                comments: line.comments,
                                lineStatus: line.line_status,
                                rejectionReason: line.rejection_reason || null,
                            });
                        }
                        // Load tasks for this project
                        loadTasks(line.project_id);
                    });
                    setDays(newDays);
                    if (result.timesheet.status === "draft") {
                        setSaveStatus("Draft loaded");
                        setTimeout(() => setSaveStatus(null), 3000);
                    }
                    setIsDirty(false);
                }
            } catch (e) {
                console.error("Failed to load draft", e);
            }
        };
        loadDraft();
    }, [weekStart, periodEnd]);

    useBlocker(({ currentLocation, nextLocation }) => {
        if (isDirty && currentLocation.pathname !== nextLocation.pathname) {
            return !window.confirm("You have unsaved changes. Are you sure you want to leave?");
        }
        return false;
    });

    const loadTasks = async (projectId: number) => {
        if (taskCache[projectId]) return;
        try {
            const tasks = await getProjectTasks(projectId);
            setTaskCache(prev => ({ ...prev, [projectId]: tasks }));
        } catch (e) {
            console.error("Failed to load tasks", e);
        }
    };

    const isTimeOffProject = (projectId: number | null): boolean => {
        if (!projectId) return false;
        const proj = projects.find(p => p.id === projectId);
        return proj?.is_whitelisted || false;
    };

    const updateEntry = (dayIdx: number, entryId: string, field: keyof TimeEntry, value: any) => {
        setIsDirty(true);
        setDays(prev => prev.map((day, i) => {
            if (i !== dayIdx) return day;
            return {
                ...day,
                entries: day.entries.map(e => {
                    if (e.id !== entryId) return e;
                    const updated = { ...e, [field]: value };
                    if (field === "projectId") {
                        updated.taskId = null;
                        if (value) loadTasks(value);
                    }
                    return updated;
                }),
            };
        }));
    };

    const addEntry = (dayIdx: number) => {
        setIsDirty(true);
        setDays(prev => prev.map((day, i) => {
            if (i !== dayIdx) return day;
            return { ...day, entries: [...day.entries, newEntry()] };
        }));
    };

    const removeEntry = (dayIdx: number, entryId: string) => {
        setIsDirty(true);
        setDays(prev => prev.map((day, i) => {
            if (i !== dayIdx) return day;
            const entries = day.entries.filter(e => e.id !== entryId);
            return { ...day, entries: entries.length ? entries : [newEntry()] };
        }));
    };

    const closeApplyPicker = () => {
        setApplyPickerEntryId(null);
        setApplyPickerDays({});
    };

    const openApplyPicker = (dayIdx: number, entry: TimeEntry) => {
        if (applyPickerEntryId === entry.id) {
            closeApplyPicker();
            return;
        }
        const initial: Record<number, boolean> = {};
        days.forEach((day, idx) => {
            if (idx === dayIdx) {
                initial[idx] = true;
            } else if (isWeekend(day.date)) {
                initial[idx] = false;
            } else {
                const hasSameEntry = days[idx].entries.some(e => e.projectId === entry.projectId && e.taskId === entry.taskId);
                initial[idx] = !hasSameEntry;
            }
        });
        setApplyPickerEntryId(entry.id);
        setApplyPickerDays(initial);
    };

    const toggleApplyPickerDay = (idx: number, currentDayIdx: number) => {
        if (idx === currentDayIdx) return;
        setApplyPickerDays(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleApplyToWeek = (sourceDayIdx: number, entry: TimeEntry) => {
        setDays(prev => {
            const newDays = prev.map(day => ({ ...day, entries: [...day.entries] }));
            newDays.forEach((_, idx) => {
                if (idx === sourceDayIdx) return;
                if (!applyPickerDays[idx]) return;
                const alreadyExists = newDays[idx].entries.some(e => e.projectId === entry.projectId && e.taskId === entry.taskId);
                if (alreadyExists) return;
                const newEntry = {
                    id: Math.random().toString(36).substr(2, 9),
                    projectId: entry.projectId,
                    taskId: entry.taskId,
                    hours: "",
                    comments: "",
                };
                const firstBlankIdx = newDays[idx].entries.findIndex(e => e.projectId === null);
                if (firstBlankIdx === -1) {
                    newDays[idx].entries.push(newEntry);
                } else {
                    newDays[idx].entries.splice(firstBlankIdx, 0, newEntry);
                }
                newDays[idx].isExpanded = true;
                if (entry.projectId) loadTasks(entry.projectId);
            });
            return newDays;
        });
        setIsDirty(true);
        closeApplyPicker();
    };

    const toggleDay = (dayIdx: number) => {
        setDays(prev => prev.map((day, i) => {
            if (i !== dayIdx) return day;
            return { ...day, isExpanded: !day.isExpanded };
        }));
    };

    const getDayTotal = (day: DayEntries): number =>
        day.entries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);

    const getWeekTotal = (): number =>
        days.reduce((sum, day) => sum + getDayTotal(day), 0);

    const getWorkingDays = (): number =>
        days.filter(d => !isWeekend(d.date)).length;

    const handleSaveDraft = async (): Promise<number | null> => {
        const start = weekStart.toISOString().split("T")[0];
        const end = periodEnd.toISOString().split("T")[0];
        const entries: any[] = [];
        days.forEach(day => {
            day.entries.forEach(entry => {
                if (!entry.projectId || !entry.taskId || !parseFloat(entry.hours)) return;
                entries.push({
                    project_id: entry.projectId,
                    task_id: entry.taskId,
                    entry_date: day.date.toISOString().split("T")[0],
                    hours: parseFloat(entry.hours),
                    comments: entry.comments,
                });
            });
        });
        try {
            setSaveStatus("Saving...");
            const result = await saveDraft({ period_start: start, period_end: end, is_partial: false, entries });
            lastSaveErrorRef.current = null;
            setSaveStatus("Draft saved!");
            setTimeout(() => setSaveStatus(null), 3000);
            setIsDirty(false);
            return result.timesheet_id;
        } catch (e: any) {
            console.error("Failed to save draft", e);
            const detail = e?.response?.data?.detail;
            const message = typeof detail === "string" ? detail : "Save failed. Please try again.";
            lastSaveErrorRef.current = message;
            setSaveStatus("Save failed");
            setTimeout(() => setSaveStatus(null), 3000);
            return null;
        }
    };

    const handleConfirmSubmit = async () => {
        setSubmitError(null);
        const timesheetId = await handleSaveDraft();
        if (!timesheetId) {
            setSubmitError(lastSaveErrorRef.current || "Failed to save draft before submitting. Please try again.");
            return;
        }
        try {
            await submitTimesheet(timesheetId);
            setIsDirty(false);
            setShowSummary(false);
            // Navigate with the success message in route state rather than
            // local component state -- this component unmounts the instant
            // navigate() fires, so a message set here would never actually
            // be seen. Home.tsx renders it as a banner instead.
            navigate("/", { state: { flashMessage: "Timesheet submitted successfully!" } });
        } catch (e: any) {
            console.error("Failed to submit timesheet", e);
            const detail = e?.response?.data?.detail;
            const message = typeof detail === "string" ? detail : "Failed to submit timesheet. Please try again.";
            setSubmitError(message);
        }
    };

    const handleExport = async () => {
        if (!timesheetId) return;
        setExportError(null);
        setExporting(true);
        try {
            const blob = await exportTimesheetXls(timesheetId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const fmt = (d: Date) => d.toISOString().split("T")[0].replace(/-/g, "");
            a.href = url;
            a.download = `Timesheet_${fmt(weekStart)}_${fmt(periodEnd)}.xls`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Failed to export timesheet", e);
            setExportError("Failed to export. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const handleCopyPreviousWeek = async () => {
        const prevPeriod = prevPeriodBounds(weekStart);
        const prevWeekStart = prevPeriod.start;
        const prevPeriodEnd = prevPeriod.end;
        const start = prevWeekStart.toISOString().split("T")[0];
        const end = prevPeriodEnd.toISOString().split("T")[0];

        try {
            setCopyStatus("Loading...");
            const result = await getDraft(start, end);
            if (!result.timesheet || result.timesheet.lines.length === 0) {
                setCopyStatus("No previous week data found");
                setTimeout(() => setCopyStatus(null), 3000);
                return;
            }

            const hasCurrentData = days.some(day =>
                day.entries.some(e => e.projectId !== null || e.taskId !== null || e.hours.trim() !== "" || e.comments.trim() !== "")
            );
            if (hasCurrentData) {
                const confirmed = window.confirm("This will overwrite your current week's entries. Continue?");
                if (!confirmed) {
                    setCopyStatus(null);
                    return;
                }
            }

            const newDays: DayEntries[] = getPeriodDates(weekStart, periodEnd).map(date => ({
                date,
                entries: [],
                isExpanded: !isFuture(date),
            }));

            // Match by day-of-week rather than position: the previous period
            // and the current period aren't guaranteed to be the same length
            // now that month-end boundaries split a normal week into shorter
            // sub-periods, so a positional index could point at the wrong
            // weekday (or past the end of a shorter period).
            result.timesheet.lines.forEach((line: any) => {
                const lineDate = new Date(line.entry_date + "T00:00:00");
                const dayIdx = newDays.findIndex(d => d.date.getDay() === lineDate.getDay());
                if (dayIdx === -1) return;
                const alreadyCopied = newDays[dayIdx].entries.some(e => e.projectId === line.project_id && e.taskId === line.task_id);
                if (alreadyCopied) return;
                newDays[dayIdx].entries.push({
                    id: Math.random().toString(36).substr(2, 9),
                    projectId: line.project_id,
                    taskId: line.task_id,
                    hours: "",
                    comments: "",
                });
                if (line.project_id) loadTasks(line.project_id);
            });

            newDays.forEach(day => {
                if (day.entries.length === 0) {
                    day.entries.push(newEntry());
                } else if (day.entries.some(e => e.projectId !== null)) {
                    day.isExpanded = true;
                }
            });

            setDays(newDays);
            setErrors({});
            setHasErrors(false);
            setIsDirty(false);
            setApplyPickerEntryId(null);
            setApplyPickerDays({});
            setCopyStatus("Previous week copied");
            setTimeout(() => setCopyStatus(null), 3000);
        } catch (e) {
            console.error("Failed to copy previous week", e);
            setCopyStatus("Copy failed");
            setTimeout(() => setCopyStatus(null), 3000);
        }
    };

    // Backlog #7 (2026-08-10, redesigned 2026-08-11 per Pete's explicit
    // request): step 1 of applying a template -- load its lines, preload
    // task names for every project referenced (so the day-picker modal
    // below can show real "Project / Task" labels immediately instead of
    // blanks that fill in a moment later), then seed a default day
    // selection per line and open the modal. Default selection mirrors
    // the old auto-apply behavior: every weekday that doesn't already have
    // that exact project/task combo, weekends left unchecked.
    const openTemplateDayPicker = async () => {
        if (!selectedTemplateId) return;
        setTemplateError(null);
        setApplyingTemplate(true);
        try {
            const detail = await getTimesheetTemplateDetail(Number(selectedTemplateId));
            const uniqueProjectIds = Array.from(new Set(detail.lines.map(l => l.project_id)));
            await Promise.all(uniqueProjectIds.map(loadTasks));

            const initialPicks: Record<number, Record<number, boolean>> = {};
            detail.lines.forEach((line, lineIdx) => {
                const dayPicks: Record<number, boolean> = {};
                days.forEach((day, dayIdx) => {
                    if (isWeekend(day.date)) {
                        dayPicks[dayIdx] = false;
                        return;
                    }
                    const alreadyExists = day.entries.some(e => e.projectId === line.project_id && e.taskId === line.task_id);
                    dayPicks[dayIdx] = !alreadyExists;
                });
                initialPicks[lineIdx] = dayPicks;
            });

            setTemplateModalLines(detail.lines);
            setTemplateModalDayPicks(initialPicks);
            setShowTemplatePicker(false);
        } catch (e) {
            console.error("Failed to load template", e);
            setTemplateError("Failed to load template. Please try again.");
        } finally {
            setApplyingTemplate(false);
        }
    };

    const toggleTemplateModalDay = (lineIdx: number, dayIdx: number) => {
        setTemplateModalDayPicks(prev => ({
            ...prev,
            [lineIdx]: { ...prev[lineIdx], [dayIdx]: !prev[lineIdx]?.[dayIdx] },
        }));
    };

    const closeTemplateDayPicker = () => {
        setTemplateModalLines(null);
        setTemplateModalDayPicks({});
        setSelectedTemplateId("");
    };

    // Step 2: write each line into every day the user checked for it.
    // Still non-destructive like the rest of this page's "apply" actions --
    // skips a day/line combo that already exists rather than overwriting it.
    const confirmApplyTemplateLines = () => {
        if (!templateModalLines) return;
        setApplyingTemplateLines(true);
        setDays(prev => {
            const newDays = prev.map(day => ({ ...day, entries: [...day.entries] }));
            templateModalLines.forEach((line, lineIdx) => {
                newDays.forEach((day, dayIdx) => {
                    if (!templateModalDayPicks[lineIdx]?.[dayIdx]) return;
                    const alreadyExists = day.entries.some(e => e.projectId === line.project_id && e.taskId === line.task_id);
                    if (alreadyExists) return;
                    const entry: TimeEntry = {
                        id: Math.random().toString(36).substr(2, 9),
                        projectId: line.project_id,
                        taskId: line.task_id,
                        hours: line.hours != null ? String(line.hours) : "",
                        comments: "",
                    };
                    const firstBlankIdx = day.entries.findIndex(e => e.projectId === null);
                    if (firstBlankIdx === -1) {
                        day.entries.push(entry);
                    } else {
                        day.entries.splice(firstBlankIdx, 0, entry);
                    }
                    day.isExpanded = true;
                });
            });
            return newDays;
        });
        setIsDirty(true);
        setApplyingTemplateLines(false);
        closeTemplateDayPicker();
    };

    const templateLineLabel = (line: TimesheetTemplateLine): string => {
        const proj = projects.find(p => p.id === line.project_id);
        const task = (taskCache[line.project_id] || []).find(t => t.id === line.task_id);
        return `${proj ? proj.name : "Unknown project"} / ${task ? task.name : "Unknown task"}`;
    };

    const validateAndShowSummary = () => {
        const newErrors: Record<string, string> = {};
        days.forEach((day, dayIdx) => {
            // Daily hour cap: 18 hrs/day, summed across every line logged
            // for that day (not per-entry) -- a day split across several
            // project/task lines that individually look fine can still add
            // up to something clearly wrong (e.g. two 12-hour lines on the
            // same day). Block review/submit on this the same way a missing
            // project/task/hours blocks it below.
            const dayTotal = getDayTotal(day);
            if (dayTotal > 18) {
                newErrors[`day-${dayIdx}`] = `Daily total of ${dayTotal.toFixed(2)} hrs exceeds the 18 hr/day cap`;
            }
            day.entries.forEach(entry => {
                const hasHours = parseFloat(entry.hours) > 0;
                const hasProject = entry.projectId !== null;
                const hasTask = entry.taskId !== null;
                const hasComment = entry.comments.trim().length > 0;
                if (!hasHours && !hasProject && !hasTask) return;
                if (!hasProject) newErrors[dayIdx + "-" + entry.id + "-project"] = "Project required";
                if (!hasTask) newErrors[dayIdx + "-" + entry.id + "-task"] = "Task required";
                if (!hasComment) newErrors[dayIdx + "-" + entry.id + "-comments"] = "Comments required";
                if (!hasHours) newErrors[dayIdx + "-" + entry.id + "-hours"] = "Hours required";
                if (hasHours) {
                    const hrs = parseFloat(entry.hours);
                    if (Math.round(hrs * 4) !== hrs * 4) {
                        newErrors[dayIdx + "-" + entry.id + "-hours"] = "Hours must be in 0.25 increments";
                    }
                }
                if (isFuture(day.date) && !isTimeOffProject(entry.projectId)) {
                    newErrors[dayIdx + "-" + entry.id + "-future"] = "Future dates only allowed for Time Off projects";
                }
                if (isWeekend(day.date) && isTimeOffProject(entry.projectId)) {
                    newErrors[dayIdx + "-" + entry.id + "-future"] = "Time Off cannot be reported on weekend dates";
                }
            });
        });
        setErrors(newErrors);
        setHasErrors(Object.keys(newErrors).length > 0);
        if (Object.keys(newErrors).length === 0) setShowSummary(true);
    };

    const goToPrevWeek = () => {
        if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to leave?")) return;
        const prev = prevPeriodBounds(weekStart);
        setWeekStart(prev.start);
        setPeriodEnd(prev.end);
    };

    const goToNextWeek = () => {
        if (isDirty && !window.confirm("You have unsaved changes. Are you sure you want to leave?")) return;
        const next = nextPeriodBounds(periodEnd);
        setWeekStart(next.start);
        setPeriodEnd(next.end);
    };

    const weekTotal = getWeekTotal();
    const workingDays = getWorkingDays();
    const avgPerDay = workingDays > 0 ? weekTotal / workingDays : 0;
    const underHours = avgPerDay < 8 && weekTotal > 0;
    // Scales with the number of weekdays actually in this period -- a
    // 2-weekday month-end sub-period should flag over 20 hrs, not the
    // full-week 50 hr threshold. Mirrors the backend's
    // timesheet_validation_service.HOURS_UPPER_PER_WEEKDAY (10 hrs/weekday).
    const overHours = weekTotal > 10 * workingDays;

    // A week is fully read-only once it's been submitted and is awaiting
    // review, or has been fully approved. A "partially_rejected" week is
    // still editable -- that's exactly the state where the consultant needs
    // to fix rejected lines and resubmit -- but individual lines that were
    // already approved OR are still awaiting review by a (possibly
    // different) Project Manager stay locked (see isEntryLocked below). A
    // timesheet can span several projects with different PMs, each
    // reviewing independently, so "partially_rejected" doesn't mean every
    // other line has been decided -- some may still be sitting at "pending"
    // with a different PM. Those need to stay locked too, otherwise the
    // consultant could edit a line that's mid-review elsewhere.
    const isReadOnly = timesheetStatus === "submitted" || timesheetStatus === "approved";
    const isPartiallyRejected = timesheetStatus === "partially_rejected";
    // "validation_rejected" -- an admin sent the entire timesheet back from
    // the Timesheet Validation screen's Check #1 -- is intentionally NOT a
    // per-line lock like partially_rejected. Every line was already reset
    // to "pending" server-side, and every line here should be fully
    // editable (not just ones with a specific line-level rejection), so
    // isEntryLocked below correctly falls through to "not locked" for this
    // status without any special-casing.
    const isValidationRejected = timesheetStatus === "validation_rejected";
    const isEntryLocked = (entry: TimeEntry): boolean =>
        isReadOnly || (isPartiallyRejected && entry.lineStatus !== "rejected" && entry.lineStatus !== undefined);
    const statusMessage = (): string => {
        const dateStr = timesheetSubmittedAt
            ? new Date(timesheetSubmittedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
            : null;
        const when = dateStr ? ` on ${dateStr}` : "";
        switch (timesheetStatus) {
            case "submitted":
                return `This timesheet was submitted${when} and is awaiting approval. It can't be edited here.`;
            case "approved":
                return `This timesheet was submitted${when} and has been approved. It can't be edited here.`;
            case "partially_rejected":
                return `This timesheet was submitted${when}. Some lines were rejected -- see the notes below, fix those lines, and resubmit. Lines already approved, or still awaiting review by a Project Manager, are locked and shown for reference only.`;
            case "validation_rejected":
                return `This timesheet was returned for revision${when}: "${validationRejectionReason || ""}" Please review your entries, make any needed changes, and resubmit.`;
            default:
                return `This timesheet has already been submitted${when} and can't be edited here.`;
        }
    };

    if (loading) return <div>Loading...</div>;

    if (showSummary) {
        return (
            <SummaryScreen
                days={days}
                projects={projects}
                taskCache={taskCache}
                weekTotal={weekTotal}
                avgPerDay={avgPerDay}
                underHours={underHours}
                overHours={overHours}
                overHoursThreshold={10 * workingDays}
                periodEnd={periodEnd}
                onBack={() => { setShowSummary(false); setSubmitError(null); }}
                onSubmit={handleConfirmSubmit}
                submitError={submitError}
            />
        );
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0, fontSize: "22px" }}>Timesheet Entry</h1>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <button onClick={goToPrevWeek} style={navBtnStyle}>
                        Prev Week
                    </button>
                    <span style={{ color: "#5C5D5E", fontWeight: "bold", fontSize: "14px" }}>
                        {weekStart.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        {weekStart.toDateString() !== periodEnd.toDateString() &&
                            " - " + periodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </span>
                    <button onClick={goToNextWeek} style={navBtnStyle}>
                        Next Week
                    </button>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#5C5D5E" }}>{weekTotal.toFixed(2)} hrs</div>
                    <div style={{ fontSize: "12px", color: underHours ? "#C00000" : "#666" }}>{avgPerDay.toFixed(2)} hrs/day avg</div>
                </div>
            </div>

            {/* 2026-08-11 (Pete's explicit request): Copy Previous Week and
                Export moved up here from the bottom action bar, so they're
                visible without scrolling past the whole week's entries. */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <button onClick={handleCopyPreviousWeek} disabled={isReadOnly || isPartiallyRejected} style={secondaryBtnStyle}>Copy Previous Week</button>
                {copyStatus && (
                    <span style={{ fontSize: "13px", color: copyStatus === "No previous week data found" || copyStatus === "Copy failed" ? "#C00000" : "#2E7D32" }}>
                        {copyStatus}
                    </span>
                )}
                {(validationStatus === "cleared" || validationStatus === "validated") && (
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        title="Export this timesheet to Excel"
                        style={{ ...secondaryBtnStyle, opacity: exporting ? 0.6 : 1 }}
                    >
                        {exporting ? "Exporting…" : "Export"}
                    </button>
                )}
                {exportError && (
                    <span style={{ fontSize: "13px", color: "#C00000" }}>{exportError}</span>
                )}
            </div>

            {(isReadOnly || isPartiallyRejected || isValidationRejected) && (
                <div style={{
                    backgroundColor: (isPartiallyRejected || isValidationRejected) ? "#FDECEA" : "#E8F4FD",
                    border: "1px solid " + ((isPartiallyRejected || isValidationRejected) ? "#C00000" : "#1565C0"),
                    borderRadius: "6px", padding: "12px 16px", marginBottom: "16px",
                    color: (isPartiallyRejected || isValidationRejected) ? "#C00000" : "#0D47A1",
                }}>
                    {statusMessage()}
                </div>
            )}

            {days.map((day, dayIdx) => {
                const dayTotal = getDayTotal(day);
                const isFutureDay = isFuture(day.date);
                return (
                    <div key={dayIdx} style={{ marginBottom: "8px", border: "1px solid #e0e0e0", borderRadius: "6px", overflow: "hidden", opacity: isFutureDay ? 0.7 : 1 }}>
                        <div onClick={() => toggleDay(dayIdx)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", backgroundColor: day.isExpanded ? "#F18E00" : "#f9f9f9", cursor: "pointer", color: day.isExpanded ? "white" : "#5C5D5E" }}>
                            <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {day.isExpanded ? "▼" : "▶"} {formatDate(day.date)}{isWeekend(day.date) ? " (Weekend)" : ""}
                            </span>
                            <span style={{ fontSize: "14px", color: errors[`day-${dayIdx}`] ? "#FFD8D8" : undefined, fontWeight: errors[`day-${dayIdx}`] ? "bold" : undefined }}>
                                {dayTotal > 0 ? dayTotal.toFixed(2) + " hrs" : "No entries"}
                            </span>
                        </div>
                        {errors[`day-${dayIdx}`] && (
                            <div style={{ ...errorStyle, fontSize: "12px", padding: "6px 16px 0 16px" }}>{errors[`day-${dayIdx}`]}</div>
                        )}
                        {day.isExpanded && (
                            <div style={{ padding: "12px 16px", backgroundColor: "white" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 80px 2fr 40px 32px", gap: "8px", marginBottom: "8px" }}>
                                    {["Project", "Task", "Hours", "Comments", "", ""].map((h, i) => (
                                        <div key={i} style={{ fontSize: "11px", fontWeight: "bold", color: "#999", textTransform: "uppercase" }}>{h}</div>
                                    ))}
                                </div>
                                {day.entries.map(entry => {
                                    const pKey = dayIdx + "-" + entry.id + "-project";
                                    const tKey = dayIdx + "-" + entry.id + "-task";
                                    const hKey = dayIdx + "-" + entry.id + "-hours";
                                    const cKey = dayIdx + "-" + entry.id + "-comments";
                                    const fKey = dayIdx + "-" + entry.id + "-future";
                                    const locked = isEntryLocked(entry);
                                    const isApprovedLocked = isPartiallyRejected && entry.lineStatus === "approved";
                                    const isPendingLocked = isPartiallyRejected && entry.lineStatus === "pending";
                                    const isRejectedLine = isPartiallyRejected && entry.lineStatus === "rejected";
                                    // Approved lines and lines still pending review (possibly with a
                                    // different Project Manager, since one timesheet can span several
                                    // projects/approvers) are both greyed out so they read as "settled
                                    // or in someone else's hands, reference only". Rejected lines get a
                                    // highlighted (not greyed) row so they stand out as the thing that
                                    // still needs action, with the rejection note called out as its own
                                    // indented line underneath rather than squeezed into the Comments
                                    // cell.
                                    const isLockedForReference = isApprovedLocked || isPendingLocked;
                                    const rowWrapStyle: React.CSSProperties = isLockedForReference
                                        ? { backgroundColor: "#F0F0F0", borderRadius: "4px", padding: "6px 8px", marginBottom: "4px" }
                                        : isRejectedLine
                                        ? { backgroundColor: "#FDECEA", borderLeft: "4px solid #C00000", borderRadius: "4px", padding: "6px 8px 6px 6px", marginBottom: "4px" }
                                        : { marginBottom: "8px" };
                                    return (
                                        <React.Fragment key={entry.id}>
                                        <div style={rowWrapStyle}>
                                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 80px 2fr 40px 32px", gap: "8px", alignItems: "start", opacity: isLockedForReference ? 0.65 : 1 }}>
                                            <div>
                                                <select value={entry.projectId || ""} onChange={e => updateEntry(dayIdx, entry.id, "projectId", e.target.value ? parseInt(e.target.value) : null)} disabled={locked} style={{ ...inputStyle, borderColor: errors[pKey] ? "#C00000" : "#ddd" }}>
                                                    <option value="">Select project...</option>
                                                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                                {errors[pKey] && <div style={errorStyle}>{errors[pKey]}</div>}
                                            </div>
                                            <div>
                                                <select value={entry.taskId || ""} onChange={e => updateEntry(dayIdx, entry.id, "taskId", e.target.value ? parseInt(e.target.value) : null)} disabled={locked || !entry.projectId} style={{ ...inputStyle, borderColor: errors[tKey] ? "#C00000" : "#ddd" }}>
                                                    <option value="">Select task...</option>
                                                    {entry.projectId && (taskCache[entry.projectId] || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                </select>
                                                {errors[tKey] && <div style={errorStyle}>{errors[tKey]}</div>}
                                            </div>
                                            <div>
                                                <input type="number" step="0.25" min="0" max="24" value={entry.hours} onChange={e => updateEntry(dayIdx, entry.id, "hours", e.target.value)} placeholder="0.00" disabled={locked} style={{ ...inputStyle, borderColor: errors[hKey] ? "#C00000" : "#ddd" }} />
                                                {errors[hKey] && <div style={errorStyle}>{errors[hKey]}</div>}
                                            </div>
                                            <div>
                                                <textarea value={entry.comments} onChange={e => updateEntry(dayIdx, entry.id, "comments", e.target.value)} placeholder="Required..." rows={1} disabled={locked} style={{ ...inputStyle, borderColor: errors[cKey] ? "#C00000" : "#ddd", resize: "none", overflow: "hidden", transition: "height 0.2s" }} onFocus={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} onBlur={e => { e.target.style.height = ""; }} onInput={e => { const t = e.target as HTMLTextAreaElement; t.style.height = "auto"; t.style.height = t.scrollHeight + "px"; }} />
                                                {errors[cKey] && <div style={errorStyle}>{errors[cKey]}</div>}
                                                {errors[fKey] && <div style={errorStyle}>{errors[fKey]}</div>}
                                            </div>
                                            <div>
                                                <button
                                                    onClick={() => openApplyPicker(dayIdx, entry)}
                                                    disabled={locked || !entry.projectId || !entry.taskId}
                                                    title="Apply to week"
                                                    style={{
                                                        width: "100%", background: "none",
                                                        border: "1px solid " + (applyPickerEntryId === entry.id ? "#F18E00" : "#ddd"),
                                                        borderRadius: "4px",
                                                        color: entry.projectId && entry.taskId ? "#5C5D5E" : "#ccc",
                                                        cursor: entry.projectId && entry.taskId ? "pointer" : "not-allowed",
                                                        fontSize: "11px", fontWeight: "bold", padding: "6px 2px",
                                                    }}
                                                >
                                                    →W
                                                </button>
                                            </div>
                                            <button onClick={() => removeEntry(dayIdx, entry.id)} disabled={locked} style={{ background: "none", border: "none", color: "#999", cursor: locked ? "default" : "pointer", fontSize: "16px", padding: "6px", opacity: locked ? 0.3 : 1 }}>x</button>
                                        </div>
                                        {isApprovedLocked && (
                                            <div style={{ fontSize: "11px", color: "#666", marginTop: "4px", marginLeft: "4px" }}>
                                                Approved — locked for reference
                                            </div>
                                        )}
                                        {isPendingLocked && (
                                            <div style={{ fontSize: "11px", color: "#666", marginTop: "4px", marginLeft: "4px" }}>
                                                Still awaiting Project Manager review — locked until reviewed
                                            </div>
                                        )}
                                        {isRejectedLine && (
                                            <div style={{ fontSize: "12px", color: "#C00000", marginTop: "4px", marginLeft: "16px" }}>
                                                ↳ Rejected{entry.rejectionReason ? ": " + entry.rejectionReason : ""} — fix and resubmit
                                            </div>
                                        )}
                                        </div>
                                        {applyPickerEntryId === entry.id && (
                                            <div style={{
                                                border: "1px solid #F18E00", borderRadius: "6px",
                                                padding: "10px 12px", marginBottom: "8px", backgroundColor: "#FFF8EC",
                                            }}>
                                                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", marginBottom: "6px", textTransform: "uppercase" }}>
                                                    Apply to week
                                                </div>
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                                                    {days.map((d, idx) => {
                                                        const isCurrentDay = idx === dayIdx;
                                                        const dayName = d.date.toLocaleDateString("en-US", { weekday: "short" });
                                                        return (
                                                            <label key={idx} style={{
                                                                display: "flex", alignItems: "center", gap: "4px",
                                                                fontSize: "12px", color: isCurrentDay ? "#aaa" : "#5C5D5E",
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isCurrentDay ? true : !!applyPickerDays[idx]}
                                                                    disabled={isCurrentDay}
                                                                    onChange={() => toggleApplyPickerDay(idx, dayIdx)}
                                                                />
                                                                {dayName}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <button onClick={() => handleApplyToWeek(dayIdx, entry)} style={{ ...primaryBtnStyle, padding: "6px 14px", fontSize: "12px" }}>Apply</button>
                                                    <button onClick={closeApplyPicker} style={{ ...secondaryBtnStyle, padding: "6px 14px", fontSize: "12px" }}>Cancel</button>
                                                </div>
                                            </div>
                                        )}
                                        </React.Fragment>
                                    );
                                })}
                                {!isReadOnly && (
                                    <button onClick={() => addEntry(dayIdx)} style={addLineBtnStyle}>+ Add Line</button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", position: "relative" }}>
                    {!isReadOnly && templates.length > 0 && (
                        <button onClick={() => setShowTemplatePicker(v => !v)} style={secondaryBtnStyle}>Apply Template</button>
                    )}
                    {showTemplatePicker && (
                        <div style={{
                            position: "absolute", top: "100%", left: 0, marginTop: "6px", zIndex: 10,
                            border: "1px solid #F18E00", borderRadius: "6px", padding: "12px 14px",
                            backgroundColor: "#FFF8EC", display: "flex", flexDirection: "column", gap: "8px", minWidth: "260px",
                        }}>
                            <select
                                value={selectedTemplateId}
                                onChange={e => setSelectedTemplateId(e.target.value ? Number(e.target.value) : "")}
                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                <option value="">Select a template...</option>
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({t.line_count} line{t.line_count === 1 ? "" : "s"})</option>
                                ))}
                            </select>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    onClick={openTemplateDayPicker}
                                    disabled={!selectedTemplateId || applyingTemplate}
                                    style={{ ...primaryBtnStyle, padding: "6px 14px", fontSize: "12px", opacity: (!selectedTemplateId || applyingTemplate) ? 0.5 : 1 }}
                                >
                                    {applyingTemplate ? "Loading…" : "Next"}
                                </button>
                                <button
                                    onClick={() => { setShowTemplatePicker(false); setSelectedTemplateId(""); setTemplateError(null); }}
                                    style={{ ...secondaryBtnStyle, padding: "6px 14px", fontSize: "12px" }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {templateError && <span style={{ fontSize: "12px", color: "#C00000" }}>{templateError}</span>}
                        </div>
                    )}
                </div>
                {!isReadOnly && (
                    <div style={{ display: "flex", gap: "12px" }}>
                        {saveStatus && (
                            <span style={{ fontSize: "13px", color: saveStatus === "Save failed" ? "#C00000" : "#2E7D32", alignSelf: "center" }}>
                                {saveStatus}
                            </span>
                        )}
                        <button onClick={handleSaveDraft} style={secondaryBtnStyle}>Save Draft</button>
                        {hasErrors && (
                            <span style={{ fontSize: "13px", color: "#C00000", alignSelf: "center" }}>
                                Please fix the errors above before submitting
                            </span>
                        )}
                        <button onClick={validateAndShowSummary} style={primaryBtnStyle}>Review and Submit</button>
                    </div>
                )}
            </div>

            {templateModalLines && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.4)", display: "flex",
                        alignItems: "center", justifyContent: "center", zIndex: 100,
                    }}
                    onClick={closeTemplateDayPicker}
                >
                    <div
                        style={{
                            backgroundColor: "white", borderRadius: "8px", padding: "24px 28px",
                            width: "560px", maxWidth: "94vw", maxHeight: "80vh", overflowY: "auto",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ margin: "0 0 4px 0", color: "#5C5D5E", fontSize: "16px" }}>Apply Template</h3>
                        <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "12px" }}>
                            Choose which days each line should be added to. Only checked days get written in --
                            days already showing that exact project/task combo are left untouched.
                        </p>
                        {templateModalLines.map((line, lineIdx) => (
                            <div key={lineIdx} style={{ marginBottom: "16px", paddingBottom: "14px", borderBottom: "1px solid #f0f0f0" }}>
                                <div style={{ fontSize: "13px", fontWeight: "bold", color: "#5C5D5E", marginBottom: "8px" }}>
                                    {templateLineLabel(line)}
                                    {line.hours != null && <span style={{ fontWeight: "normal", color: "#999" }}> ({line.hours} hrs)</span>}
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {days.map((day, dayIdx) => (
                                        <label key={dayIdx} style={{
                                            display: "flex", alignItems: "center", gap: "4px",
                                            fontSize: "12px", color: "#5C5D5E",
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={!!templateModalDayPicks[lineIdx]?.[dayIdx]}
                                                onChange={() => toggleTemplateModalDay(lineIdx, dayIdx)}
                                            />
                                            {day.date.toLocaleDateString("en-US", { weekday: "short" })}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
                            <button onClick={closeTemplateDayPicker} style={secondaryBtnStyle} disabled={applyingTemplateLines}>Cancel</button>
                            <button
                                onClick={confirmApplyTemplateLines}
                                disabled={applyingTemplateLines}
                                style={{ ...primaryBtnStyle, opacity: applyingTemplateLines ? 0.6 : 1 }}
                            >
                                {applyingTemplateLines ? "Applying…" : "Apply"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface SummaryProps {
    days: DayEntries[];
    projects: Project[];
    taskCache: Record<number, Task[]>;
    weekTotal: number;
    avgPerDay: number;
    underHours: boolean;
    overHours: boolean;
    overHoursThreshold: number;
    periodEnd: Date;
    onBack: () => void;
    onSubmit: () => void;
    submitError: string | null;
}

const SummaryScreen: React.FC<SummaryProps> = ({ days, projects, taskCache, weekTotal, avgPerDay, underHours, overHours, overHoursThreshold, periodEnd, onBack, onSubmit, submitError }) => {
    // Under-40 or over-50 hours for the week isn't necessarily wrong (PTO
    // weeks, crunch weeks, etc.) but it's unusual enough that submitting it
    // by accident should require an explicit second confirmation, not just a
    // banner the user can miss. Clicking Confirm and Submit here opens a
    // modal instead of submitting directly when either condition applies;
    // the modal's own "Yes, Submit" is what actually calls onSubmit.
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const isAbnormalHours = underHours || overHours;

    const handleSubmitClick = () => {
        if (isAbnormalHours) {
            setShowConfirmModal(true);
        } else {
            onSubmit();
        }
    };
    const rowKeys: string[] = [];
    const rowLabels: Record<string, string> = {};
    const rowSortKeys: Record<string, { projectName: string; taskName: string }> = {};
    const grid: Record<string, Record<number, number>> = {};

    days.forEach((day, dayIdx) => {
        day.entries.forEach(entry => {
            if (!entry.projectId || !entry.taskId || !parseFloat(entry.hours)) return;
            const proj = projects.find(p => p.id === entry.projectId);
            const task = (taskCache[entry.projectId] || []).find(t => t.id === entry.taskId);
            const key = entry.projectId + "-" + entry.taskId;
            if (!rowKeys.includes(key)) {
                rowKeys.push(key);
                const projectName = proj ? proj.name : "";
                const taskName    = task ? task.name : "";
                rowLabels[key]   = projectName + " / " + taskName;
                rowSortKeys[key] = { projectName, taskName };
            }
            if (!grid[key]) grid[key] = {};
            grid[key][dayIdx] = (grid[key][dayIdx] || 0) + parseFloat(entry.hours);
        });
    });

    // Sort alphabetically by Project name, then Task name, so the review
    // table doesn't just reflect whatever order entries happened to be typed in.
    rowKeys.sort((a, b) => {
        const byProject = rowSortKeys[a].projectName.localeCompare(rowSortKeys[b].projectName);
        if (byProject !== 0) return byProject;
        return rowSortKeys[a].taskName.localeCompare(rowSortKeys[b].taskName);
    });

    const dayTotals = days.map((_, dayIdx) =>
        rowKeys.reduce((sum, key) => sum + (grid[key] ? (grid[key][dayIdx] || 0) : 0), 0)
    );

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", marginTop: 0, fontSize: "22px" }}>Review Your Timesheet</h1>
            <p style={{ color: "#666", marginBottom: "24px" }}>
                {days[0] && (
                    (days[0].date.toDateString() === periodEnd.toDateString()
                        ? "Week of "
                        : "Period ") +
                    days[0].date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) +
                    (days[0].date.toDateString() !== periodEnd.toDateString()
                        ? " - " + periodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                        : "")
                )}
            </p>
            {underHours && (
                <div style={{ backgroundColor: "#FFF2CC", border: "1px solid #F18E00", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", color: "#7F6000" }}>
                    {"Your timesheet averages " + avgPerDay.toFixed(2) + " hrs/day (" + weekTotal.toFixed(2) + " hrs total). Standard is 8.0 hrs/day. Are you sure you want to submit?"}
                </div>
            )}
            {overHours && (
                <div style={{ backgroundColor: "#FDECEA", border: "1px solid #C00000", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", color: "#C00000" }}>
                    {"Your timesheet totals " + weekTotal.toFixed(2) + " hrs this period -- over " + overHoursThreshold + " hrs. Are you sure you want to submit?"}
                </div>
            )}
            <div style={{ overflowX: "auto", marginBottom: "24px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#5C5D5E", color: "white" }}>
                            <th style={{ ...thStyle, textAlign: "left", width: "280px" }}>Project / Task</th>
                            {days.map((day, i) => (
                                <th key={i} style={thStyle}>{day.date.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })}</th>
                            ))}
                            <th style={thStyle}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rowKeys.map((key, rowIdx) => {
                            const rowTotal = days.reduce((sum, _, dayIdx) => sum + (grid[key] ? (grid[key][dayIdx] || 0) : 0), 0);
                            return (
                                <tr key={key} style={{ backgroundColor: rowIdx % 2 === 0 ? "white" : "#FDE8BC" }}>
                                    <td style={{ ...tdStyle, textAlign: "left" }}>{rowLabels[key]}</td>
                                    {days.map((_, dayIdx) => (
                                        <td key={dayIdx} style={tdStyle}>{grid[key] && grid[key][dayIdx] ? grid[key][dayIdx].toFixed(2) : "—"}</td>
                                    ))}
                                    <td style={{ ...tdStyle, fontWeight: "bold" }}>{rowTotal.toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr style={{ backgroundColor: "#5C5D5E", color: "white", fontWeight: "bold" }}>
                            <td style={{ ...tdStyle, textAlign: "left" }}>Daily Total</td>
                            {dayTotals.map((total, i) => (
                                <td key={i} style={tdStyle}>{total > 0 ? total.toFixed(2) : "—"}</td>
                            ))}
                            <td style={tdStyle}>{weekTotal.toFixed(2)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            {submitError && (
                <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "12px" }}>{submitError}</div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button onClick={onBack} style={secondaryBtnStyle}>Go Back and Edit</button>
                <button onClick={handleSubmitClick} style={primaryBtnStyle}>Confirm and Submit</button>
            </div>

            {showConfirmModal && (
                <div style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.4)", display: "flex",
                    alignItems: "center", justifyContent: "center", zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: "white", borderRadius: "8px", padding: "24px 28px",
                        maxWidth: "420px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    }}>
                        <h2 style={{ margin: "0 0 12px 0", fontSize: "17px", color: "#5C5D5E" }}>
                            Confirm unusual hours
                        </h2>
                        <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#333" }}>
                            {overHours
                                ? `This timesheet totals ${weekTotal.toFixed(2)} hrs this period -- over ${overHoursThreshold} hrs.`
                                : `This timesheet averages ${avgPerDay.toFixed(2)} hrs/day (${weekTotal.toFixed(2)} hrs total) -- under the 8.0 hrs/day standard.`}
                            {" "}Are you sure you want to submit as-is?
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button onClick={() => setShowConfirmModal(false)} style={secondaryBtnStyle}>Cancel</button>
                            <button
                                onClick={() => { setShowConfirmModal(false); onSubmit(); }}
                                style={primaryBtnStyle}
                            >
                                Yes, Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetEntry;
